import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Fingerprint } from "lucide-react";
import { ApiClientError } from "../api/http.js";
import { confirmMasterPassword, describeAuthError } from "../app/authFlow.js";
import { confirmWithPasskey, passkeyUnlockAvailable } from "../app/passkeyUnlock.js";
import { useSession } from "../app/session.js";
import { setStepUpHandler } from "../app/stepUp.js";
import type { StepUpKind } from "../app/stepUp.js";
import { Dialog } from "../ui/Dialog.js";
import { Alert, Button } from "../ui/primitives.js";
import { Field, SecretInput } from "../ui/forms.js";

function stepUpError(err: unknown): string {
  if (err instanceof ApiClientError && err.status === 404) {
    return "Für dein Konto ist kein Passkey eingerichtet. Bestätige mit deinem Master-Passwort.";
  }
  return describeAuthError(err);
}

/**
 * Asks for the master password or a passkey before a sensitive change (new second factor, backup
 * codes, team key, recovery). Opened by the request layer when the server answers REAUTH_REQUIRED.
 */
export function StepUpDialog(): ReactNode {
  const { state } = useSession();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<StepUpKind>("any");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const settle = useRef<((confirmed: boolean) => void) | undefined>(undefined);
  const unlocked = state.status === "unlocked" ? state : undefined;

  useEffect((): (() => void) | undefined => {
    if (unlocked === undefined) {
      return undefined;
    }
    const unregister = setStepUpHandler((next: StepUpKind): Promise<boolean> => new Promise<boolean>((resolve): void => {
      settle.current = resolve;
      setKind(next);
      setError(undefined);
      setOpen(true);
    }));
    return (): void => {
      unregister();
      settle.current?.(false);
      settle.current = undefined;
      setOpen(false);
    };
  }, [unlocked]);

  if (!open || unlocked === undefined) {
    return null;
  }
  const { email, client } = unlocked;

  function close(confirmed: boolean): void {
    settle.current?.(confirmed);
    settle.current = undefined;
    setOpen(false);
    setPassword("");
    setError(undefined);
  }

  async function run(check: () => Promise<void>): Promise<void> {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await check();
      close(true);
    } catch (err: unknown) {
      setError(stepUpError(err));
    } finally {
      setBusy(false);
      setPassword("");
    }
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (password !== "") {
      void run((): Promise<void> => confirmMasterPassword(client, email, password));
    }
  }

  return (
    <Dialog
      open
      onClose={(): void => close(false)}
      title="Änderung bestätigen"
      description={kind === "passkey"
        ? "Diese Aktion verlangt eine Bestätigung mit einem Passkey (Fingerabdruck, Gesicht oder PIN). Das Master-Passwort reicht hier nicht."
        : "Diese Änderung betrifft die Sicherheit deines Kontos. Bestätige sie mit deinem Master-Passwort oder einem Passkey."}
    >
      <form onSubmit={submit} className="flex flex-col gap-5 p-5">
        {kind === "any" ? (
          <>
            <Field id="stepup-password" label="Master-Passwort">
              <SecretInput
                id="stepup-password"
                data-autofocus
                autoComplete="current-password"
                value={password}
                onChange={(event): void => setPassword(event.target.value)}
                revealLabel="Master-Passwort"
              />
            </Field>
            {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
            <Button type="submit" loading={busy} disabled={password === ""} className="w-full" size="lg">
              {busy ? "Wird geprüft …" : "Bestätigen"}
            </Button>
          </>
        ) : error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
        {kind === "passkey" || passkeyUnlockAvailable() ? (
          <Button variant="secondary" icon={Fingerprint} disabled={busy} className="w-full" onClick={(): void => void run(confirmWithPasskey)}>
            Mit Passkey bestätigen
          </Button>
        ) : null}
        <Button variant="ghost" disabled={busy} className="w-full" onClick={(): void => close(false)}>
          Abbrechen
        </Button>
      </form>
    </Dialog>
  );
}
